import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import {
  VibesitesImage,
  getMediaUrl,
  parseStorageKey,
  videoThumbStorageKey,
} from '@vibesites/ui-primitives';

/**
 * Via6 property Hero override.
 *
 * Forked from the Weidner Via6 theme Hero so the Home hero can play the
 * property featured video as a full-bleed background (with image fallback).
 * Keeps the parent layout: property title + CTA, no Ken Burns / parallax,
 * and the header-offset sizing below the fixed nav.
 */
const featuredMediaDef: SlotGroupDefinition = {
  label: 'Background Media',
  description: 'If a video is set, it plays as the background. Otherwise the image is used.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    video: {
      label: 'Video',
      type: 'video',
      defaultField: 'property_featured_video',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bg = useSlotGroup('hero.bg_image', featuredMediaDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);

  const showVideo = !!bg.video;
  const thumbKey = videoThumbStorageKey(parseStorageKey(bg.video || null));
  const poster =
    (thumbKey ? getMediaUrl(thumbKey, { width: 1920 }) : null) || bg.image || '';

  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setReducedMotion(mq.matches);
      if (mq.matches) videoRef.current?.pause();
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return (
    <section className="hero-section relative w-full overflow-hidden">
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredMediaDef}>
        <div className="absolute inset-0">
          {showVideo ? (
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              src={bg.video || undefined}
              poster={poster}
              autoPlay={!reducedMotion}
              muted
              loop
              playsInline
              aria-hidden
            />
          ) : bg.image ? (
            <VibesitesImage
              url={bg.image}
              alt=""
              priority
              sizes="100vw"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Gradient overlay for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      {/* Content */}
      <div className="hero-content relative z-10 flex flex-col items-start justify-center h-full text-left text-white px-6 sm:px-12 md:px-16 lg:px-24">
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p>Luxury Apartments<br>in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-2 hero-title-glow font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal mb-5 max-w-5xl tracking-tight drop-shadow-lg uppercase"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-4 hero-cta group/cta relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-medium tracking-[0.2em] uppercase text-white overflow-hidden transition-all duration-300"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        /* ============================================================
           Header offset — push the hero below the fixed rise Header
           (and any active SpecialOfferBanner) instead of letting its
           background image slide up behind the now-solid Via6 nav bar.

           Heights match the row-height overrides in Header.tsx:
             - <1024px:  80px main nav row only
             - >=1024px: 140px (104px main + 36px h-9 util row)
           Plus the dynamic SpecialOfferBanner height published on :root
           by rise's banner component as --offer-banner-height (defaults
           to 0px when no banner is active).

           Section fills the remaining viewport height (100vh minus the
           header offset) so the hero spans the full visible area below
           the nav, and the CTA stays in-frame at the bottom.
           ============================================================ */
        .hero-section {
          margin-top: calc(80px + var(--offer-banner-height, 0px));
          height: calc(100vh - 80px - var(--offer-banner-height, 0px));
        }
        @media (min-width: 1024px) {
          .hero-section {
            margin-top: calc(140px + var(--offer-banner-height, 0px));
            /* Taller hero on desktop — add 140px beyond the visible-below-nav
               area so the section extends a bit past the initial fold. */
            height: calc(100vh - 140px - var(--offer-banner-height, 0px) + 140px);
          }
        }

        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-entrance-4 { animation-delay: 0.8s; }
        .hero-cta {
          background: transparent;
          border: 1.5px solid rgba(255, 255, 255, 0.75);
          border-radius: 2px;
          color: #fff;
          box-shadow: none;
        }
        .hero-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(255, 255, 255, 0);
          transition: background 0.3s ease;
        }
        .hero-cta:hover {
          background: var(--vibe-color-secondary);
          border-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-primary);
          box-shadow: none;
          transform: translateY(-2px);
        }
        .hero-cta:hover::before {
          background: transparent;
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
        .hero-title-glow {
          line-height: 1.35;
        }
        @media (min-width: 1024px) {
          .hero-title-glow {
            line-height: 1.4;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>Luxury Apartments<br>in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredMediaDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Hero;
